import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Camera } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs, { toLdItems } from "@/components/Breadcrumbs";
import { buildBreadcrumbLd } from "@/lib/seo";
import { publishedRealisations } from "@/data/realisations";
import { useSeoT, PAGE_META } from "@/lib/seoPagesI18n";

export default function RealisationsIndex() {
  const navigate = useNavigate();
  const { t: st, lang } = useSeoT();
  const rt = st.realisations;
  const bc = [{ name: st.bc.home, path: "/" }, { name: st.bc.realisations, path: "/realisations" }];
  const items = publishedRealisations();
  const noindex = items.length === 0;

  return (
    <div className="min-h-screen bg-white text-[#0A0F2C]" data-testid="realisations-index">
      <Seo title={(PAGE_META[lang] || PAGE_META.fr).realisations.title} description={(PAGE_META[lang] || PAGE_META.fr).realisations.desc} path="/realisations" noindex={noindex} jsonLd={buildBreadcrumbLd(toLdItems(bc))} />
      <Header />
      <section className="relative bg-[#0A0F2C] text-white overflow-hidden pt-28 md:pt-36 pb-14 md:pb-20 px-5 sm:px-8 md:px-10 lg:px-14">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(45,91,255,0.4), transparent 55%)" }} />
        <div className="relative mx-auto" style={{ maxWidth: "1280px" }}>
          <div className="mb-6"><Breadcrumbs items={bc} variant="dark" /></div>
          <h1 className="font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{rt.h1}</h1>
          <p className="mt-5 text-white/80 text-base md:text-lg max-w-2xl">{rt.heroSub}</p>
        </div>
      </section>
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "1280px" }}>
          {items.length === 0 ? (
            <div className="text-center max-w-xl mx-auto" data-testid="realisations-empty">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#EAF0FF] text-[#2D5BFF]"><Camera size={26} /></div>
              <h2 className="mt-5 font-extrabold text-2xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{rt.emptyTitle}</h2>
              <p className="mt-3 text-[#4B5563] leading-relaxed">{rt.emptyText}</p>
              <button onClick={() => navigate("/devis")} className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#0A0F2C] hover:bg-[#1a2050] text-white px-7 py-4 font-extrabold text-sm transition-all" data-testid="realisations-cta">
                {rt.cta} <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((r) => (
                <div key={r.slug} className="rounded-2xl border border-gray-100 overflow-hidden" data-testid={`realisation-${r.slug}`}>
                  {r.imagesAfter?.[0] && <img src={r.imagesAfter[0]} alt={`${r.service} à ${r.city}`} className="w-full h-48 object-cover" loading="lazy" />}
                  <div className="p-5">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">{r.service} · {r.city}</div>
                    <p className="mt-2 text-sm text-[#4B5563]">{r.result}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
